/**
 * Shared shell styles for the recipient-facing pages.
 *
 * Shared so the standalone entry points render an identical card and cannot drift
 * apart visually.
 */
export const shellStyles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
    // Stop mobile browsers resizing the text on their own. Inflation is applied per
    // block container rather than inherited, so this has to sit on the page root here
    // as well as in onboarding-form.css - the gate is a separate surface with its own
    // inline styles and does not load that stylesheet.
    WebkitTextSizeAdjust: '100%',
    textSizeAdjust: '100%',
    // Match the form's rendering weight; iOS otherwise thickens the strokes.
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  logoHeader: { backgroundColor: '#f8f9fa', padding: '5px 30px' },
    titleBar: {
      backgroundColor: 'var(--company-primary-color, #273947)',
      color: 'var(--company-primary-contrast, #ffffff)',
      textAlign: 'center',
      padding: '8px 12px',
    fontSize: 'clamp(12px, 2.8vw, 16px)',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 'clamp(0.15px, 0.08vw, 0.5px)',
    lineHeight: '1.3',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  prefillBanner: {
    backgroundColor: '#e8f4f8',
    color: '#00485f',
    textAlign: 'center',
    padding: '6px 12px',
    fontSize: 'clamp(11px, 2.4vw, 13px)',
    borderBottom: '1px solid #cfe3ea',
  },
  backButton: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'var(--company-overlay, rgba(255,255,255,0.15))',
    border: '1px solid var(--company-overlay, rgba(255,255,255,0.3))',
    color: 'var(--company-primary-contrast, white)',
    padding: '4px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    textTransform: 'none',
    letterSpacing: 'normal'
  },
  centeredBody: { padding: '20px 30px', backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 80px)' },
  cardWrap: { textAlign: 'center', padding: '50px 20px', maxWidth: '1000px', margin: '0 auto' },
  card: {
    background: 'white',
    padding: '40px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    maxWidth: '720px',
    margin: '0 auto'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '15px',
    boxSizing: 'border-box'
  },
  primaryBtn: {
    padding: '12px 30px',
    backgroundColor: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #ffffff)',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px'
  },
  secondaryBtn: {
    padding: '12px 24px',
    backgroundColor: '#eef2f7',
    color: '#0f172a',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px'
  },
  authViewport: {
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 16px 0'
  },
  authCard: {
    width: '100%',
    maxWidth: '500px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.10)'
  },
  authHero: {
    backgroundColor: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #ffffff)',
    padding: '20px 24px 18px',
    textAlign: 'center'
  },
  authLogoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '8px 14px',
    marginBottom: '16px'
  },
  authLogo: {
    maxHeight: '56px',
    width: 'auto',
    display: 'block'
  },
  authHeroTitle: {
    margin: 0,
    fontSize: 'clamp(16px, 3vw, 21px)',
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  authHeroSubtitle: {
    margin: '10px 0 0',
    fontSize: '14px',
    lineHeight: 1.4,
    color: 'var(--company-primary-contrast, rgba(255,255,255,0.96))'
  },
  authBody: {
    padding: '20px 24px'
  },
  authInfoBox: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    backgroundColor: '#eaf4fb',
    border: '1px solid #b8daf3',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#0c6f91',
    fontSize: '13px',
    lineHeight: 1.4,
    marginBottom: '16px'
  },
  authInfoIcon: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
    borderRadius: '999px',
    backgroundColor: '#0c7aa3',
    color: '#ffffff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '11px',
    marginTop: '2px'
  },
  authLabel: {
    display: 'block',
    marginBottom: '8px',
    color: '#404040',
    fontWeight: 400,
    fontSize: '14px'
  },
  authInput: {
    width: '100%',
    height: '42px',
    padding: '0 14px',
    borderRadius: '6px',
    border: '1px solid #d9dee5',
    fontSize: '14px',
    color: '#1f2937',
    boxSizing: 'border-box',
    outline: 'none',
    marginBottom: '16px'
  },
  authPrimaryBtn: {
    width: '100%',
    height: '42px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #ffffff)',
    fontWeight: 700,
    fontSize: '14px',
    cursor: 'pointer'
  },
  authSecondaryLinkBtn: {
    width: '100%',
    marginTop: '12px',
    background: 'transparent',
    border: 'none',
    color: 'var(--company-primary-color, #273947)',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer'
  },
  authFooter: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#8a8f98',
    padding: '16px 0',
    width: '100%'
  },
  authHintText: {
    marginTop: '12px',
    marginBottom: '16px',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#5f6368'
  }
};
